import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { UPDATE_PRIERE_SUCCESS } from '../lib/actions/priereJanazaActions';
import { uploadPreuve, deletePreuve } from '../lib/api/preuveApi';
import {
  PREUVE_ACCEPT, preuveUri, useCanManagePreuve, validatePreuveFile, uploadErrorKey,
} from '../lib/preuve';
import './Preuve.css';

// Preuve d'une janaza (flyer d'origine), comme sur l'app mobile :
// consultation pour tous, ajout / modification / suppression pour l'auteur ou un admin.

function IconEye({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
    </svg>
  );
}

function IconEdit({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
      <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
    </svg>
  );
}

function IconTrash({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
      <path d="M10 11v6M14 11v6M9 6V4h6v2"/>
    </svg>
  );
}

function IconAttach({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"/>
    </svg>
  );
}

function IconShield() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 2l8 3v6c0 5-3.4 9.4-8 11-4.6-1.6-8-6-8-11V5l8-3zm-1.2 13.6l5.7-5.7-1.4-1.4-4.3 4.3-2.1-2.1-1.4 1.4 3.5 3.5z"/>
    </svg>
  );
}

// Sélecteur de fichier caché ; pick() ouvre la fenêtre du navigateur et renvoie le fichier validé (ou null)
function useFilePicker() {
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const resolverRef = useRef(null);

  const input = (
    <input
      ref={inputRef}
      type="file"
      accept={PREUVE_ACCEPT}
      style={{ display: 'none' }}
      onChange={(e) => {
        const file = e.target.files?.[0] ?? null;
        e.target.value = '';
        const errorKey = validatePreuveFile(file);
        if (errorKey) window.alert(t(errorKey));
        resolverRef.current?.(errorKey ? null : file);
        resolverRef.current = null;
      }}
    />
  );

  const pick = () => new Promise((resolve) => {
    resolverRef.current = resolve;
    inputRef.current?.click();
  });

  return { input, pick };
}

// Ajout / remplacement / suppression d'une preuve en ligne, avec mise à jour du store
function usePreuveActions(priere, onChanged) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const dbId = useSelector((s) => s.auth.user?.dbId);
  const { input, pick } = useFilePicker();
  const [busy, setBusy] = useState(null); // 'upload' | 'delete'

  const apply = (patch) => {
    dispatch({ type: UPDATE_PRIERE_SUCCESS, payload: { ...priere, ...patch } });
    onChanged?.(patch);
  };

  async function replace() {
    const file = await pick();
    if (!file) return false;
    setBusy('upload');
    try {
      const res = await uploadPreuve(priere.id, file, dbId);
      apply({ preuveUrl: res.data.preuveUrl, preuveType: res.data.preuveType });
      return true;
    } catch (err) {
      window.alert(t(uploadErrorKey(err)));
      return false;
    } finally {
      setBusy(null);
    }
  }

  async function remove() {
    if (!window.confirm(`${t('preuve.delete_confirm_title')}\n${t('preuve.delete_confirm_body')}`)) return false;
    setBusy('delete');
    try {
      await deletePreuve(priere.id, dbId);
      apply({ preuveUrl: null, preuveType: null });
      return true;
    } catch {
      window.alert(t('preuve.error_delete'));
      return false;
    } finally {
      setBusy(null);
    }
  }

  return { busy, replace, remove, input };
}

// Fenêtre « La preuve » : src + isPdf ; boutons d'action seulement si onEdit/onDelete sont fournis
function PreuveSheet({ src, isPdf, onClose, onEdit, onDelete, busy, extra }) {
  const { t } = useTranslation();
  const [state, setState] = useState('loading');
  useEffect(() => setState('loading'), [src]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return createPortal(
    <div className="preuve-overlay" onClick={onClose}>
      <div className="preuve-sheet" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={t('preuve.title')}>
        <div className="preuve-header">
          <span className="preuve-title-icon"><IconShield /></span>
          <span className="preuve-title">{t('preuve.title')}</span>
          <button type="button" className="preuve-close" onClick={onClose} aria-label="✕">✕</button>
        </div>

        <div className="preuve-body">
          {src && (isPdf ? (
            <iframe className="preuve-pdf" src={src} title={t('preuve.title')} onLoad={() => setState('ready')} />
          ) : (
            <img
              className="preuve-img"
              src={src}
              alt={t('preuve.title')}
              onLoad={() => setState('ready')}
              onError={() => setState('error')}
            />
          ))}
          {state === 'loading' && <div className="preuve-status"><span className="preuve-spinner" /></div>}
          {state === 'error' && <div className="preuve-status">{t('preuve.error_load')}</div>}
        </div>

        {isPdf && src && (
          <a className="preuve-open-tab" href={src} target="_blank" rel="noopener noreferrer">{t('preuve.open_new_tab')}</a>
        )}

        {onEdit && onDelete && (
          <div className="preuve-footer">
            <button type="button" className="preuve-btn preuve-btn-edit" onClick={onEdit} disabled={!!busy}>
              {busy === 'upload' ? <span className="preuve-spinner" /> : <><IconEdit /> {t('preuve.edit')}</>}
            </button>
            <button type="button" className="preuve-btn preuve-btn-delete" onClick={onDelete} disabled={!!busy}>
              {busy === 'delete' ? <span className="preuve-spinner preuve-spinner-red" /> : <><IconTrash /> {t('preuve.delete')}</>}
            </button>
          </div>
        )}
        {extra}
      </div>
    </div>,
    document.body,
  );
}

// Preuve en ligne
export function PreuveViewerModal({ priere, onClose, onChanged }) {
  const canManage = useCanManagePreuve(priere);
  const { busy, replace, remove, input } = usePreuveActions(priere, onChanged);
  return (
    <PreuveSheet
      src={preuveUri(priere)}
      isPdf={priere.preuveType === 'pdf'}
      onClose={onClose}
      busy={busy}
      onEdit={canManage ? replace : null}
      onDelete={canManage ? async () => { if (await remove()) onClose(); } : null}
      extra={input}
    />
  );
}

// Les listes peuvent garder une copie figée de la prière : on applique la dernière modification faite ici
function useLivePreuve(priere, onChanged) {
  const [override, setOverride] = useState(null);
  useEffect(() => setOverride(null), [priere?.id, priere?.preuveUrl]);
  const current = override ? { ...priere, ...override } : priere;
  return [current, (p) => { setOverride(p); onChanged?.(p); }];
}

// Icône œil à droite du bouton partager ; rien sans preuve
export function PreuveEyeButton({ priere, className = 'pc-share', size = 16 }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [current, handleChanged] = useLivePreuve(priere);
  if (!current?.preuveUrl && !open) return null;
  return (
    <>
      {!!current?.preuveUrl && (
        <button type="button" className={className} onClick={() => setOpen(true)} title={t('preuve.view')} aria-label={t('preuve.view')}>
          <IconEye size={size} />
        </button>
      )}
      {open && <PreuveViewerModal priere={current} onClose={() => setOpen(false)} onChanged={handleChanged} />}
    </>
  );
}

// Fenêtres de modification : « Voir la preuve » + crayon + corbeille, ou « Ajouter la preuve »
export function PreuveEditRow({ priere }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [current, handleChanged] = useLivePreuve(priere);
  const { busy, replace, remove, input } = usePreuveActions(priere, handleChanged);
  if (!priere?.id) return null;

  if (!current?.preuveUrl) {
    return (
      <div className="preuve-row">
        <button type="button" className="preuve-add-btn" onClick={replace} disabled={!!busy}>
          {busy === 'upload' ? <span className="preuve-spinner" /> : <><IconAttach /> {t('preuve.add')}</>}
        </button>
        {input}
      </div>
    );
  }

  return (
    <div className="preuve-row">
      <button type="button" className="preuve-view-btn" onClick={() => setOpen(true)} disabled={!!busy}>
        <IconEye /> {t('preuve.view')}
      </button>
      <button type="button" className="preuve-icon-btn" onClick={replace} disabled={!!busy} title={t('preuve.edit')} aria-label={t('preuve.edit')}>
        {busy === 'upload' ? <span className="preuve-spinner" /> : <IconEdit size={18} />}
      </button>
      <button type="button" className="preuve-icon-btn preuve-icon-btn-delete" onClick={remove} disabled={!!busy} title={t('preuve.delete')} aria-label={t('preuve.delete')}>
        {busy === 'delete' ? <span className="preuve-spinner preuve-spinner-red" /> : <IconTrash size={18} />}
      </button>
      {input}
      {open && <PreuveViewerModal priere={current} onClose={() => setOpen(false)} onChanged={handleChanged} />}
    </div>
  );
}

// Formulaire de déclaration : la prière n'existe pas encore, le fichier est gardé jusqu'à la publication
export function PreuvePickRow({ file, onChange }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { input, pick } = useFilePicker();
  const localUrl = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  useEffect(() => () => { if (localUrl) URL.revokeObjectURL(localUrl); }, [localUrl]);
  const choose = async () => { const f = await pick(); if (f) onChange(f); };

  if (!file) {
    return (
      <div className="preuve-row">
        <button type="button" className="preuve-add-btn" onClick={choose}>
          <IconAttach /> {t('preuve.add')} <span className="preuve-add-hint">{t('preuve.optional')}</span>
        </button>
        {input}
      </div>
    );
  }

  return (
    <div className="preuve-row">
      <button type="button" className="preuve-view-btn" onClick={() => setOpen(true)}>
        <IconEye /> {t('preuve.view')}
      </button>
      <button type="button" className="preuve-icon-btn" onClick={choose} title={t('preuve.edit')} aria-label={t('preuve.edit')}>
        <IconEdit size={18} />
      </button>
      <button type="button" className="preuve-icon-btn preuve-icon-btn-delete" onClick={() => onChange(null)} title={t('preuve.delete')} aria-label={t('preuve.delete')}>
        <IconTrash size={18} />
      </button>
      {input}
      {open && (
        <PreuveSheet
          src={localUrl}
          isPdf={file.type === 'application/pdf'}
          onClose={() => setOpen(false)}
          onEdit={choose}
          onDelete={() => { setOpen(false); onChange(null); }}
        />
      )}
    </div>
  );
}
